import React from 'react'
import { useHistory } from 'react-router-dom'
import { Formik } from 'formik'
import { Divider, Form, Button, Row, Col } from '_zari/core'
import Yup from '_zari/validation/_yup'

import { ZariContext } from '_zari/context'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import ListaMonedasConstant from 'constants/listaDeMonedas'

import { DateHandler, IdGen, RounderUtil, ZrSwal } from 'utils'

import * as SaldosInicialesCrud from 'services/saldosIniciales.service'

import { RetrocederButton } from 'sharedComponents/buttons'

import MonedaFormGroup from './formGroups/moneda'
import FechaEmisionFormGroup from './formGroups/fechaEmision'
import TituloFormGroup from './formGroups/titulo'
import TipoCambioFormGroup from './formGroups/tipoCambio'

import DetallesSection from './sections/detalles'

const validationSchema = Yup.object().shape({
  moneda: Yup.string().required(),
  titulo: Yup.string().required(),
  fechaEmision: Yup.string().required()
})

const actualizarFila = (current, { cantidad, precio, ...restPayload }) => {
  current = {
    ...current,
    ...restPayload,
    cantidad,
    precio
  }

  current.subTotal = RounderUtil.round(current.cantidad * current.precio)

  return current
}

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          __focusDescuento: false,
          __focusPrecio: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion)

      let { viaCodBar, _id, ...payload } = action.payload
      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idProductoPresentacion: _id,
          _idFila: IdGen.newId(),
          cantidad: '1'
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: `${IdGen.newId()}-G`
        }
      ]
    }
    case 'actualizar': {
      const { aplicarTodos, saltarFila, ...nuevoPayload } = action.payload
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload)
          }
          val.__filaActiva = !saltarFila
        }
        return val
      })
    }
    case 'actualizarComentario':
      return state.map((val, i) => {
        if (val._idFila === action.payload._idFila) {
          val.comentario = action.payload.comentario
        }
        return val
      })
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const MainForm = ({ editarData }) => {
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, inputDets => {
    if (inputDets) {
      inputDets = inputDets.map(x => ({
        ...x,
        __filaActiva: false,
        __focusCantidad: false,
        __focusDescuento: false,
        __focusPrecio: false,
        _idFila: IdGen.newId()
      }))

      return [
        ...inputDets,
        {
          esFilaBusqueda: true,
          _idFila: IdGen.newId()
        }
      ]
    }

    return [
      {
        esFilaBusqueda: true,
        _idFila: IdGen.newId()
      }
    ]
  })

  const formInitialValues = React.useMemo(() => {
    // TODO - Admin context
    const nuevaFecha = DateHandler.format(new Date())

    return {
      moneda: editarData.moneda || ListaMonedasConstant.SOLES,
      fechaEmision: editarData.fechaEmision || nuevaFecha,
      titulo: editarData.titulo || '',
      tipoCambio: editarData.tipoCambio || 1
    }
  }, [editarData])

  const submitHandler = React.useCallback(
    async data => {
      const detallesNormalizados = detalles
        .filter(x => x._idProductoPresentacion)
        .map(x => {
          return {
            _idProductoPresentacion: x._idProductoPresentacion,
            cantidad: parseFloat(x.cantidad),
            precio: parseFloat(x.precio)
          }
        })

      if (editarData._id) {
        await SaldosInicialesCrud.actualizarxId(editarData._id, {
          ...data,
          detalles: detallesNormalizados
        })
          .then(() => {
            __zariContext.snack('success', 'El registro se ha actualizado.')
            history.push(`/${CodigosTransaccionesConstant.SALDOSINICIALES_LISTA}`)
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      } else {
        await SaldosInicialesCrud.nuevoSaldoInicial({
          ...data,
          detalles: detallesNormalizados
        })
          .then(() => {
            __zariContext.snack('success', 'El registro se ha agregado.')
            history.push(`/${CodigosTransaccionesConstant.SALDOSINICIALES_LISTA}`)
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      }
    },
    [detalles, __zariContext, history, editarData._id]
  )

  const clickEliminar = React.useCallback(() => {
    ZrSwal.showAlert({
      tipo: 'error',
      mensaje: 'Desea eliminar el registro?',
      showCerrar: true,
      onSuccess: () => {
        __zariContext.loader.open('Eliminando registro')
        SaldosInicialesCrud.eliminarSaldoInicial(editarData._id)
          .then(() => {
            __zariContext.snack('success', 'El registro se ha eliminado.')
            history.push(`/${CodigosTransaccionesConstant.SALDOSINICIALES_LISTA}`)
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error)).finally(() => {
            __zariContext.loader.close()
          })
      }
    })
  }, [editarData._id, __zariContext, history])

  return (
    <Row>
      <Col md={12}>
        <Formik initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={validationSchema}>
          {({ errors, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
            <>
              <Form>
                <Row>
                  <Col md={4}>
                    <FechaEmisionFormGroup fIsSubmitting={isSubmitting} iError={errors.fechaEmision} iTouched={touched.fechaEmision} />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <MonedaFormGroup fIsSubmitting={isSubmitting} iError={errors.moneda} iTouched={touched.moneda} />
                  </Col>
                  <Col md={2}>
                    <TipoCambioFormGroup
                      fIsSubmitting={isSubmitting}
                      iError={errors.tipoCambio}
                      iTouched={touched.tipoCambio}
                      iFechaDocumento={values.fechaEmision}
                      iMoneda={values.moneda}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <TituloFormGroup fIsSubmitting={isSubmitting} iError={errors.titulo} iTouched={touched.titulo} />
                  </Col>
                </Row>
                <Divider />
                <DetallesSection detalles={detalles} dispatch={detallesDispatcher} />
                <Divider />
                <Row>
                  <Col md={6}>
                    {editarData._id && (
                      <Button disabled={isSubmitting} onClick={clickEliminar} color='danger'>
                        Eliminar
                      </Button>
                    )}
                  </Col>
                  <Col md={6} className='text-right'>
                    <RetrocederButton />
                    <Button disabled={isSubmitting} onClick={handleSubmit} className='ml-2'>
                      Guardar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Formik>
      </Col>
    </Row>
  )
}

export default MainForm
